import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
} from '@mui/material';
import { ArrowBack, Download } from '@mui/icons-material';
import { trainingManagementService, CourseAnalytics } from '../services/trainingManagementService';

export default function CourseAnalyticsPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();

  const [analytics, setAnalytics] = useState<CourseAnalytics | null>(null);
  const [learners, setLearners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (courseId) {
      loadAnalytics();
      loadLearners();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const loadAnalytics = async () => {
    if (!courseId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await trainingManagementService.getCourseAnalytics(courseId);
      setAnalytics(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadLearners = async () => {
    if (!courseId) return;

    try {
      const data = await trainingManagementService.getCourseLearners(courseId, { limit: 100 });
      setLearners(data.learners);
    } catch (err: any) {
      console.error('Failed to load learners:', err);
    }
  };

  const handleExport = () => {
    // TODO: Implement CSV export
    alert('Export functionality coming soon!');
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !analytics) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Analytics not found'}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/training')}
          sx={{ mt: 2 }}
        >
          Back to Training Management
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/training')}
          sx={{ mb: 2 }}
        >
          Back to Training Management
        </Button>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Course Analytics
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {analytics.courseName}
            </Typography>
          </Box>
          <Button startIcon={<Download />} variant="outlined" onClick={handleExport}>
            Export Data
          </Button>
        </Box>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant="h3" fontWeight="bold">
              {analytics.totalEnrollments}
            </Typography>
            <Typography variant="body2">Total Enrollments</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, bgcolor: 'success.main', color: 'white' }}>
            <Typography variant="h3" fontWeight="bold">
              {analytics.completionRate}%
            </Typography>
            <Typography variant="body2">Completion Rate</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, bgcolor: 'warning.main', color: 'white' }}>
            <Typography variant="h3" fontWeight="bold">
              {analytics.averageQuizScore}%
            </Typography>
            <Typography variant="body2">Avg Quiz Score</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, bgcolor: 'secondary.main', color: 'white' }}>
            <Typography variant="h3" fontWeight="bold">
              {analytics.certificatesIssued}
            </Typography>
            <Typography variant="body2">Certificates Issued</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Additional Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Completion Funnel
            </Typography>
            <Box mt={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Enrolled</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {analytics.totalEnrollments}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={100}
                sx={{ height: 8, borderRadius: 1, mb: 2 }}
              />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Completed</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {analytics.completedCount}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={analytics.completionRate}
                color="success"
                sx={{ height: 8, borderRadius: 1, mb: 2 }}
              />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Passed Quiz</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {Math.round((analytics.quizPassRate / 100) * analytics.completedCount)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={analytics.quizPassRate}
                color="warning"
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Quiz Performance
            </Typography>
            <Box mt={2}>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="body2">Average Score</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {analytics.averageQuizScore}%
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="body2">Pass Rate</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {analytics.quizPassRate}%
                </Typography>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  Based on {analytics.completedCount} completions
                </Typography>
              </Alert>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Learner List */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Enrolled Learners ({learners.length})
        </Typography>

        {learners.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No learners enrolled yet
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Learner</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Progress</TableCell>
                  <TableCell>Enrolled</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell align="center">Quiz Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {learners.map((learner) => (
                  <TableRow key={learner.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {learner.user?.firstName} {learner.user?.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {learner.user?.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={learner.status}
                        size="small"
                        color={
                          learner.status === 'COMPLETED'
                            ? 'success'
                            : learner.status === 'IN_PROGRESS'
                            ? 'primary'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ minWidth: 100 }}>
                        <Typography variant="caption">
                          {learner.videoDuration > 0
                            ? Math.round((learner.videoProgress / learner.videoDuration) * 100)
                            : 0}
                          %
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={
                            learner.videoDuration > 0
                              ? (learner.videoProgress / learner.videoDuration) * 100
                              : 0
                          }
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {learner.createdAt
                          ? new Date(learner.createdAt).toLocaleDateString()
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {learner.completedAt
                          ? new Date(learner.completedAt).toLocaleDateString()
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={learner.quizPassed ? 'success.main' : 'text.secondary'}
                      >
                        {learner.quizScore !== null ? `${learner.quizScore}%` : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
}

